import { captureException } from '@sentry/react';
import type { MouseEventHandler } from 'react';
import invariant from 'tiny-invariant';

import type { Mode } from 'src/modules/designStudio/providers';
import { useCurrentPage, useMode } from 'src/shared/hooks';
import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import type { ButtonPressed, LinkPressed } from 'src/shared/telemetry';
import { buttonPressed } from 'src/shared/telemetry';
import { isDevelopment } from 'src/shared/utils/environment';

interface CommonTrackingProperties {
  page_or_screen_name: string;
  primary_cta: boolean;
  button_location?: string;
  button_text: string;
  design_studio_mode: Mode;
  job_id?: number;
  partner_id?: number;
  button_or_link: string;
}

export type ButtonTrackHandler = (properties: CommonTrackingProperties) => void;
type LinkTrackHandler = (
  properties: LinkPressed & {
    button_or_link?: string;
    button_text: string;
  },
) => void;

export type OptionalTrackingProperties = Partial<
  Omit<ButtonPressed, 'design_studio_mode'> & {
    job_id?: number;
    destination_url?: string;
    link_location?: string;
    link_text?: string;
  }
>;

export interface UseTrackingProps {
  buttonLocation?: string;
  buttonText?: string;
  optionalTrackingProperties?: OptionalTrackingProperties;
  pageName?: string;
  primaryCallToAction?: boolean;
  track?: ButtonTrackHandler | LinkTrackHandler;
}

/**
 * Add tracking functionality to anything with a click handler
 *
 * @see {@link ../../components/Button}
 *
 * @returns object with `onClick` handler to invoke for tracking call
 */
const useTracking = ({
  buttonLocation,
  buttonText,
  optionalTrackingProperties,
  pageName,
  primaryCallToAction = false,
  track,
}: UseTrackingProps) => {
  const currentPage = useCurrentPage();
  const {
    metadata: { job_id: jobId, partner_id: partnerId },
  } = useTrackingMetadata();

  const page = pageName || currentPage;

  // TODO move mode context to a Design Studio specific button,
  // as it's not needed for other generic tracking cases
  const mode = useMode();

  const onClick: MouseEventHandler<HTMLElement> = () => {
    try {
      // Allow button_text to be set via buttonText argument or button_text in optionalTrackingProperties
      const trackingButtonText =
        optionalTrackingProperties?.button_text ?? buttonText;

      invariant(page, 'Button: pageName is required when current page is null');
      invariant(
        trackingButtonText,
        'Button: label is required when children is not string',
      );

      const properties = {
        page_or_screen_name: page,
        primary_cta: primaryCallToAction,
        button_location: buttonLocation,
        button_text: trackingButtonText,
        design_studio_mode: mode,
        partner_id: partnerId,
        ...optionalTrackingProperties,
        job_id: jobId ?? optionalTrackingProperties?.job_id ?? undefined,
      };

      if (track) {
        track({
          button_or_link: 'button',
          link_text: properties.link_text || trackingButtonText,
          ...properties,
        });
      } else {
        buttonPressed(properties);
      }
    } catch (error) {
      if (isDevelopment()) {
        throw new Error(
          `Failed to track. Missing tracking property \`${
            page ? 'buttonText' : 'page'
          }.\``,
          {
            cause: error,
          },
        );
      }

      captureException(error);
    }
  };

  return { onClick };
};

export { useTracking };
